import { Link, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ShoppingBag, User, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navigation = [
  { name: "Behandlungen", href: "/behandlungen" },
  { name: "Behandlungsinfos", href: "/behandlungen-info" },
  { name: "Shop", href: "/shop" },
  { name: "Marken", href: "/marken" },
  { name: "Blog", href: "/blog" },
  { name: "Über mich", href: "/ueber-mich" },
  { name: "Kontakt", href: "/kontakt" },
];

export const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { itemCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      {/* Navigation - Minimal Editorial */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream py-4 shadow-sm"
            : "bg-transparent py-6"
        }`}
      >
        <nav className="mx-auto max-w-7xl px-6 lg:px-12" aria-label="Global">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <span
                className="text-lg tracking-[0.08em] text-charcoal"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                data-testid="nav-brand-logo"
              >
                The Beauty Bar Tamara
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:gap-x-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-xs uppercase tracking-widest transition-colors whitespace-nowrap ${
                    isActive(item.href)
                      ? "text-[#1a1a1a] font-medium"
                      : `${scrolled ? 'text-[#4a4a4a] hover:text-[#1a1a1a]' : 'text-[#1a1a1a]/70 hover:text-[#1a1a1a]'}`
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-6">
              {/* Cart */}
              <Link
                to="/shop/warenkorb"
                className={`relative transition-colors ${scrolled ? 'text-[#1a1a1a]' : 'text-[#1a1a1a]'} hover:text-terracotta`}
                data-testid="cart-button"
              >
                <ShoppingBag className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 h-5 w-5 bg-terracotta text-white text-xs flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>

              {/* Book Now Button - Desktop */}
              <Link to="/termin-buchen" className="hidden lg:block">
                <Button
                  className="bg-charcoal hover:bg-charcoal-light text-cream px-6 py-5 text-xs uppercase tracking-widest"
                  data-testid="book-now-header-btn"
                >
                  Buchen
                </Button>
              </Link>

              {/* Mobile menu button */}
              <button
                type="button"
                className="lg:hidden text-charcoal"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-cream border-t border-charcoal/10">
            <div className="px-6 py-8 space-y-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block text-lg uppercase tracking-widest ${
                    isActive(item.href)
                      ? "text-charcoal"
                      : "text-warm-gray"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer - Modern Minimal Elegant */}
      <footer className="bg-[#3D2B1F] text-white">
        {/* Main Footer Content */}
        <div className="mx-auto max-w-7xl px-8 lg:px-16">
          {/* Top Section - Brand & CTA */}
          <div className="py-20 lg:py-28 border-b border-white/[0.08]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
              {/* Brand */}
              <div>
                <h3 
                  className="text-5xl lg:text-6xl text-white mb-4 tracking-tight"
                >
                  The Beauty Bar Tamara
                </h3>
                <p className="text-[#D4C4B0] font-light text-base max-w-md leading-relaxed">
                  Professionelle Kosmetik & Permanent Make-up
                </p>
              </div>
              
              {/* CTA Button */}
              <div className="lg:text-right">
                <Link to="/termin-buchen">
                  <Button 
                    className="bg-[#A8B5A0] text-[#3D2B1F] hover:bg-[#8A9A7C] px-10 py-6 text-sm uppercase tracking-[0.2em] font-medium transition-all duration-300"
                    data-testid="footer-book-btn"
                  >
                    Termin vereinbaren
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Middle Section - 3 Column Layout */}
          <div className="py-16 lg:py-20 grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20">
            {/* Navigation */}
            <div>
              <p className="text-[#D4C4B0]/50 uppercase tracking-[0.3em] text-[10px] font-medium mb-8">
                Navigation
              </p>
              <ul className="space-y-4">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-[#D4C4B0]/80 hover:text-white transition-colors duration-300 text-[15px] font-light"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <p className="text-[#D4C4B0]/50 uppercase tracking-[0.3em] text-[10px] font-medium mb-8">
                Kontakt
              </p>
              <div className="space-y-6">
                <div>
                  <p className="text-white text-[15px] font-light">Tamara Belcastro</p>
                  <p className="text-[#D4C4B0]/60 text-sm font-light">Kosmetikerin EFZ</p>
                </div>
                <div className="text-[#D4C4B0]/80 text-[15px] font-light space-y-1">
                  <p>Kohlholzweg 11a</p>
                  <p>4614 Hägendorf</p>
                </div>
                <div className="text-[#D4C4B0]/80 text-[15px] font-light space-y-1">
                  <a href="tel:+41765225060" className="hover:text-white transition-colors duration-300 block">
                    076 522 50 60
                  </a>
                  <a href="mailto:thebeautybar@mail.ch" className="hover:text-white transition-colors duration-300 block">
                    thebeautybar@mail.ch
                  </a>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div>
              <p className="text-[#D4C4B0]/50 uppercase tracking-[0.3em] text-[10px] font-medium mb-8">
                Öffnungszeiten
              </p>
              <div className="space-y-4">
                <div className="flex justify-between text-[15px] font-light">
                  <span className="text-[#D4C4B0]/60">Mo & Di</span>
                  <span className="text-[#D4C4B0]">13:00 – 21:00</span>
                </div>
                <div className="flex justify-between text-[15px] font-light">
                  <span className="text-[#D4C4B0]/60">Mi & Do</span>
                  <span className="text-[#D4C4B0]">10:00 – 17:30</span>
                </div>
                <div className="flex justify-between text-[15px] font-light">
                  <span className="text-[#D4C4B0]/60">Fr</span>
                  <span className="text-[#D4C4B0]">10:00 – 16:00</span>
                </div>
                <div className="flex justify-between text-[15px] font-light">
                  <span className="text-[#D4C4B0]/60">Sa & So</span>
                  <span className="text-[#D4C4B0]">Geschlossen</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="py-8 border-t border-[#D4C4B0]/10 flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-[#D4C4B0]/40 text-[11px] tracking-wider uppercase">
              © {new Date().getFullYear()} The Beauty Bar Tamara
            </p>
            <div className="flex items-center gap-10">
              <Link
                to="/impressum"
                className="text-[#D4C4B0]/40 hover:text-[#D4C4B0] transition-colors duration-300 text-[11px] tracking-wider uppercase"
              >
                Impressum
              </Link>
              <Link
                to="/agb"
                className="text-[#D4C4B0]/40 hover:text-[#D4C4B0] transition-colors duration-300 text-[11px] tracking-wider uppercase"
              >
                AGB
              </Link>
              <a
                href="https://www.easywebswiss.ch/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D4C4B0]/40 hover:text-[#D4C4B0] transition-colors duration-300 text-xs tracking-wider uppercase"
              >
                Made with <span className="text-purple-400 text-base">❤️</span> by easywebswiss.ch
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Fixed admin access dot - bottom right */}
      <Link
        to="/admin/login"
        className="fixed bottom-5 right-5 w-4 h-4 rounded-full bg-[#3D2B1F]/30 hover:bg-[#3D2B1F]/70 transition-all cursor-pointer z-50"
        data-testid="hidden-admin-dot"
        aria-label="Admin"
      />
    </div>
  );
};
